import React from 'react';
import {
  Body, StopCard,
} from '@routific/hive';
import { observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';
import usePermissions, { Features } from '../../../../../../permissions/usePermissions';
import ComponentNames from '../../../../../../permissions/constants/componentNames';
import TimelineDot from '../../../../../shared/TimelineDot';
import { RouteDtoModelType, RouteTimelineDtoModelType } from '../../../../../../store/mst/models';
import StartEndTimelineCard from './StartEndTimelineCard';
import VisitTimelineCard from './VisitTimelineCard';
import { DropzonePosition } from './TimelineCardDropZone';
import TimelineDragListItem, { DragDropProps } from './TimelineDragListItem';
import { HeaderEllipsisCaption, HeaderTextStack } from './HeaderTextStyles';

interface Props {
  route: RouteDtoModelType;
  event?: RouteTimelineDtoModelType;
  index: number;
  dragDropProps?: DragDropProps;
  stateless?: boolean;
  hideDraggableIcon?: boolean;
  isOtherDragging?: boolean;
  isBreakdownLoading?: boolean;
  disableDrag?: boolean;
  onDeliveryStopSelect?: (event: RouteTimelineDtoModelType, e: React.MouseEvent<HTMLDivElement>) => void;
}

const TimelineCard: React.FunctionComponent<Props> = ({
  route, event, index, dragDropProps, stateless, hideDraggableIcon, isOtherDragging, isBreakdownLoading, disableDrag = false, onDeliveryStopSelect,
}) => {
  const { t } = useTranslation('common');

  const {
    canAssignStopsToRoutes,
  } = usePermissions(Features.Routes, ComponentNames.TimelineCard);

  // Set the target to the dragged over card and identify dropzone position if either on top of a card or the bottom
  const handleMouseOver = (e: React.MouseEvent<HTMLElement>) => {
    let target = e.target as Element;
    if (!Number(target.getAttribute('data-item-index'))) {
      const closestElement = target.closest('[data-item-index]');

      if (!closestElement) {
        return;
      }

      target = closestElement;
    }

    const rect = target.getBoundingClientRect();
    const mouseY = e.clientY;

    if (dragDropProps?.dragOriginIndex !== undefined) {
      dragDropProps.setDraggedOverIndex(Number(target.getAttribute('data-item-index')));
      dragDropProps.setDropzonePosition(mouseY < rect.top + rect.height / 2 ? DropzonePosition.TOP : DropzonePosition.BOTTOM);
    }
  };

  const handleMouseLeave = () => {
    dragDropProps?.setDropzonePosition(undefined);
    dragDropProps?.setDraggedOverIndex(undefined);
  };

  const getStatelessEventCard = (text: string) => (
    <StopCard
      marker={<TimelineDot eventType={event?.eventType} color={route.color} status={event?.currentStatus?.status} eventSummary={event?.eventSummary} />}
      header={<Body>{text}</Body>}
      isComplete={!!event?.isDeparted}
    />
  );

  const getBreakEventCard = (e: RouteTimelineDtoModelType) => {
    const timeWindow = e.startTimeString && e.endTimeString ? `${e.startTimeString} - ${e.endTimeString}` : undefined;

    return (
      <StopCard
        marker={<TimelineDot eventType={e.eventType} color={route.color} status={e.currentStatus?.status} eventSummary={e.eventSummary} />}
        header={(
          <HeaderTextStack data-testid="break-timeline-card-header" data-cy="break-timeline-card-header">
            {timeWindow && <HeaderEllipsisCaption type="secondary">{timeWindow}</HeaderEllipsisCaption>}
            <HeaderEllipsisCaption>{t('events.paused')}</HeaderEllipsisCaption>
          </HeaderTextStack>
        )}
        isComplete={!!e.isDeparted}
      />
    );
  };

  const getDraggableCard = (e: RouteTimelineDtoModelType) => (canAssignStopsToRoutes && dragDropProps && !stateless
    ? (
      <TimelineDragListItem
        event={e}
        index={index}
        dragDropProps={dragDropProps}
        isDragDisabled={disableDrag}
        onMouseOver={isOtherDragging ? handleMouseOver : undefined}
        onMouseLeave={handleMouseLeave}
      >
        {({ provided, snapshot }: any) => (
          <VisitTimelineCard
            event={e}
            routeColor={route.color}
            stateless={stateless}
            hideDraggableIcon={hideDraggableIcon}
            dragHandleProps={provided.dragHandleProps}
            isDraggable={e.isDraggable && !disableDrag}
            isDragging={snapshot.isDragging}
            isOtherDragging={isOtherDragging}
            isBreakdownLoading={isBreakdownLoading}
            onDeliveryStopSelect={onDeliveryStopSelect}
          />
        )}
      </TimelineDragListItem>
    ) : (
      <VisitTimelineCard
        event={e}
        routeColor={route.color}
        stateless={stateless}
        hideDraggableIcon={hideDraggableIcon}
        isBreakdownLoading={isBreakdownLoading}
        onDeliveryStopSelect={onDeliveryStopSelect}
      />
    ));

  const getDraggableStartEnd = (e: RouteTimelineDtoModelType) => (canAssignStopsToRoutes && dragDropProps ? (
    <TimelineDragListItem
      event={e}
      index={index}
      dragDropProps={dragDropProps}
      onMouseOver={isOtherDragging ? handleMouseOver : undefined}
      onMouseLeave={handleMouseLeave}
    >
      {() => (
        <StartEndTimelineCard route={route} event={e} />
      )}
    </TimelineDragListItem>
  ) : <StartEndTimelineCard route={route} event={e} />);

  switch (event?.eventType) {
    case 'leaving_start_location':
    case 'reaching_end_location': {
      return getDraggableStartEnd(event);
    }
    case 'delivering_stop': {
      return getDraggableCard(event);
    }
    case 'idling': {
      return getStatelessEventCard(t(`events.${event?.eventType}`));
    }
    case 'paused': {
      return getBreakEventCard(event);
    }
    default:
      return null;
  }
};

export default observer(TimelineCard);
